import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Calendar, ArrowRight, Clock, Tag } from "lucide-react";
import { newsUpdates } from "../mock";

const NewsSection = () => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      "Technology & Transparency": "bg-pink-100 text-pink-700",
      "Workers Welfare": "bg-green-100 text-green-700",
      "Child Development": "bg-blue-100 text-blue-700",
      "Women Empowerment": "bg-purple-100 text-purple-700",
      "Scheme Updates": "bg-yellow-100 text-yellow-700"
    };
    return colors[category] || "bg-gray-100 text-gray-700";
  };

  return (
    <section id="news" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full mb-6">
            <Calendar className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            WCD Schemes & Updates
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Stay informed about Gujarat's women & child development programs, scheme updates, and success stories
          </p>
        </div>

        {/* News Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {newsUpdates.map((news, index) => (
            <Card key={index} className="group hover:shadow-xl transition-all duration-300 transform hover:scale-105 cursor-pointer">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between mb-3">
                  <Badge className={getCategoryColor(news.category)}>
                    <Tag className="w-3 h-3 mr-1" />
                    {news.category}
                  </Badge>
                  <div className="flex items-center text-gray-500 text-sm">
                    <Clock className="w-4 h-4 mr-1" />
                    {formatDate(news.date)}
                  </div>
                </div>
                <CardTitle className="text-lg font-bold text-gray-900 group-hover:text-pink-600 transition-colors leading-tight">
                  {news.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  {news.summary}
                </p>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-pink-600 hover:text-pink-700 hover:bg-pink-50 p-0 h-auto font-semibold"
                >
                  Read Full Story
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </CardContent>
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-pink-500 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </Card>
          ))}
        </div>

        {/* Newsletter Signup */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-8 text-center">
          <div className="max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-white mb-4">
              Stay Updated with Gujarat WCD
            </h3>
            <p className="text-gray-300 mb-6">
              Subscribe to our newsletter for the latest updates on WCD schemes, policy changes, and women & child development progress.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email address"
                className="flex-1 px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500 backdrop-blur-sm"
              />
              <Button className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-semibold px-8 py-3">
                Subscribe
              </Button>
            </div>
            <p className="text-gray-400 text-sm mt-4">
              Weekly updates • No spam • Unsubscribe anytime
            </p>
          </div>
        </div>

        {/* Latest Announcements */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Recent Announcements
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-pink-50 border-l-4 border-pink-500 p-6 rounded-lg">
              <div className="flex items-center mb-3">
                <div className="w-2 h-2 bg-pink-500 rounded-full mr-3 animate-pulse" />
                <span className="text-pink-700 font-semibold text-sm">SCHEME UPDATE</span>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">
                Anganwadi Worker Wages Increased to Rs. 24,800
              </h4>
              <p className="text-gray-600 text-sm">
                Gujarat High Court mandates significant wage increase for 1.06 lakh Anganwadi workers and helpers across the state.
              </p>
            </div>
            <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-lg">
              <div className="flex items-center mb-3">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-3 animate-pulse" />
                <span className="text-green-700 font-semibold text-sm">MILESTONE ACHIEVED</span>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">
                2 Lakh Beneficiaries Reached through Matrushakti Yojana
              </h4>
              <p className="text-gray-600 text-sm">
                First 1000 days nutrition program successfully provides comprehensive support to mothers and children across Gujarat.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsSection;